/**
 * shareCard — renders a branded, shareable PNG card on an offscreen <canvas>.
 *
 * Three card types drive the app's cheapest growth loop (a shared image that
 * makes the second person's install one scan away):
 *   • "trade"     — a completed / proposed swap (each trainer's name over the
 *                   Pokémon they hand over, shiny badges). Wired to the Trade
 *                   Match "Share trade".
 *   • "milestone" — a Dex completion moment (e.g. Living Dex 100%). Wired to the
 *                   collection-milestone notification row.
 *   • "dexNeeds"  — the Pokédex page's "who has these?" ask: the two completion
 *                   rings, "Do you have any of these Pokémon for trading?", and
 *                   3 random missing released species. Unlike the trade card
 *                   this one is AIMED at people who may not run the app (it
 *                   goes to a group chat / Discord), so its footer IS the
 *                   official App Store badge — the store URL rides in the
 *                   caption, since a PNG has no links.
 *
 * The visual language mirrors the app's promo cards: a dark-green radial glow,
 * floating bokeh, neon-green Live-Scan corner brackets framing the subject, the
 * 3D compass medallion, a bold headline, and a footer with a QR + nexusdex.ai
 * CTA (Nexus Dex mark + wordmark). The milestone card's caption carries a
 * tappable store URL; the trade card's caption is one plain line and no link —
 * it goes to a friend who is already running the app.
 *
 * Everything is drawn imperatively so it works inside the Capacitor WebView with
 * no DOM-to-image dependency. Remote sprites are CORS-fetched (never tainting the
 * canvas) with a graceful name-chip fallback, so toBlob() can always export.
 */
import medallionUrl from "@/assets/nexus_dex_medallion_3d.png";
import logoMarkUrl from "@/assets/nexus_dex_logo_mark.png";
import appStoreBadgeUrl from "@/assets/appstore-badge.svg";
import { officialArtworkUrl, homeArtworkUrl } from "@/lib/pokemonArtwork";
import { isMegaGmaxVariant, megaGmaxSpriteCandidates } from "@/lib/megaGmaxSprites";
import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";
import { getVariantLabel } from "@/components/dexVariantVisuals";

export interface TradeCardSide {
  speciesId: number;
  name: string;
  variant: string;
}
export interface TradeCardSpec {
  kind: "trade";
  give: TradeCardSide;
  get: TradeCardSide;
  fromName: string;
  toName: string;
  /** Overrides the default "Let's trade!" headline. */
  headline?: string;
}
export interface MilestoneCardSpec {
  kind: "milestone";
  headline: string; // e.g. "Living Dex\nComplete"
  subtitle: string; // e.g. "Johto · Shiny"
  percentLabel: string; // e.g. "100%"
}
/** One completion donut, mirroring DexCompletionRing's props + colour ladder. */
export interface DexNeedsRing {
  percent: number; // 0..100 float; displayed value truncates like the ring
  caught: number;
  total: number;
  label: string; // e.g. "POKEMON GO"
  sublabel?: string; // e.g. "RELEASED"
}
export interface DexNeedsCardSpec {
  kind: "dexNeeds";
  rings: [DexNeedsRing, DexNeedsRing];
  /** Up to 3 missing released species, sprites drawn full colour. The variant
   *  pill under each sprite (e.g. ✦ SHINY) is the only dex-variant marker on
   *  the card — a header label duplicating it was removed for height. */
  needs: TradeCardSide[];
}
export type ShareCardSpec = TradeCardSpec | MilestoneCardSpec | DexNeedsCardSpec;

/**
 * Live App Store listing. For cards shared at people who may NOT have the app —
 * the milestone brag card goes to anyone.
 */
export const APP_STORE_URL = "https://apps.apple.com/us/app/nexus-dex/id6767402880";

/**
 * The site, drawn in the card footer.
 *
 * A trade card is only ever sent to a friend you are connected to inside Nexus
 * Dex — the trade could not have been surfaced otherwise — so an App Store link
 * is telling someone to install what they are already running. The site is the
 * useful thing to point at, and it is where a store link lives anyway if they
 * are somehow on the other platform. It rides on the CARD only: the trade
 * caption is one line of text and carries no link at all.
 */
export const SITE_HOST = "nexusdex.ai";

export interface ShareCardResult {
  blob: Blob;
  dataUrl: string;
  width: number;
  height: number;
}

// Sizes/colours/font are exported for sibling canvas renderers (the TCG-style
// Pokémon card in pokemonTcgCard.ts) so every shared image speaks one brand.
const W = 1080;
const H = 1920;
/**
 * The dexNeeds card is ~4:5, not 9:16: it is built for iMessage / chat
 * bubbles, which crop a 9:16 image top and bottom in the preview (observed
 * on-device: the wordmark and the App Store badge were both cut off). This
 * ratio renders whole in an iMessage bubble.
 */
const DEX_NEEDS_H = 1300;
export const NEON = "#39f58f";
export const INK = "#eafff4";
export const DIM = "#9fc9b4";
export const FONT = '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

const SHINY_VARIANTS = new Set(["shiny", "shundo", "shinyLucky"]);
const isShinyVariant = (v: string): boolean => SHINY_VARIANTS.has(v);

// ── Public API ───────────────────────────────────────────────────────────────

export async function renderShareCard(
  spec: ShareCardSpec,
): Promise<ShareCardResult> {
  const height = spec.kind === "dexNeeds" ? DEX_NEEDS_H : H;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d canvas context unavailable");

  // Let any CSS-declared webfont (Inter) settle so headline metrics are right.
  try {
    await (document as Document & { fonts?: FontFaceSet }).fonts?.ready;
  } catch {
    /* fonts API absent — system fallback is fine */
  }

  drawBackground(ctx, height);
  drawParticles(ctx, height);

  if (spec.kind === "trade") {
    drawWordmark(ctx);
    await drawTradeBody(ctx, spec);
    await drawFooter(ctx);
  } else if (spec.kind === "milestone") {
    drawWordmark(ctx);
    await drawMilestoneBody(ctx, spec);
    await drawFooter(ctx);
  } else {
    // dexNeeds draws its own compact header + footer: the official App Store
    // badge. This card is aimed at people who may not have the app, so the
    // store CTA earns the footer slot the friend-to-friend cards give to our
    // own branding.
    await drawDexNeedsBody(ctx, spec);
  }

  const blob = await canvasToBlob(canvas);
  return { blob, dataUrl: canvas.toDataURL("image/png"), width: W, height };
}

// ── Card bodies ──────────────────────────────────────────────────────────────

async function drawTradeBody(ctx: CanvasRenderingContext2D, spec: TradeCardSpec): Promise<void> {
  drawHeadline(ctx, spec.headline ?? "Let's trade!", 430);

  const tile = 300;
  const gap = 150; // space for the arrow
  const groupW = tile * 2 + gap;
  const leftX = (W - groupW) / 2;
  const rightX = leftX + tile + gap;
  const tileY = 760;

  // Brand scan-frame around the whole pair.
  drawScanFrame(ctx, W / 2, tileY + tile / 2, groupW + 96, tile + 130);

  const [giveImg, getImg] = await Promise.all([
    loadSpriteForSide(spec.give),
    loadSpriteForSide(spec.get),
  ]);

  // Each trainer's name sits over the Pokémon THEY hand over (2026-10-07
  // owner ask). The card travels to the other trainer, so "You give" read as
  // the receiver and pointed the swap the wrong way; the names cannot.
  drawSpriteTile(ctx, leftX, tileY, tile, spec.give, giveImg, spec.fromName);
  drawSpriteTile(ctx, rightX, tileY, tile, spec.get, getImg, spec.toName);

  // Swap arrow between the tiles.
  drawSwapArrow(ctx, W / 2, tileY + tile / 2);
}

async function drawMilestoneBody(ctx: CanvasRenderingContext2D, spec: MilestoneCardSpec): Promise<void> {
  drawHeadline(ctx, spec.headline, 400);

  const frame = 520;
  const cy = 940;
  drawScanFrame(ctx, W / 2, cy, frame, frame);

  const medallion = await loadSameOrigin(medallionUrl);
  if (medallion) {
    const s = frame * 0.78;
    drawImageContain(ctx, medallion, W / 2 - s / 2, cy - s / 2, s, s);
  }

  // Big completion chip under the frame.
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = `800 132px ${FONT}`;
  ctx.fillStyle = NEON;
  withGlow(ctx, NEON, 34, () => ctx.fillText(spec.percentLabel, W / 2, cy + frame / 2 + 190));

  ctx.font = `600 42px ${FONT}`;
  ctx.fillStyle = DIM;
  ctx.fillText(spec.subtitle, W / 2, cy + frame / 2 + 258);
}

async function drawDexNeedsBody(ctx: CanvasRenderingContext2D, spec: DexNeedsCardSpec): Promise<void> {
  // Compact layout (DEX_NEEDS_H) so the whole card survives an iMessage
  // bubble: wordmark, rings, headline, framed tiles, store badge. No dex
  // label (the ✦ variant pill under each sprite already says it) and no
  // site-host line (the badge is the CTA) — both were removed for height.
  drawWordmark(ctx, 96);

  // The two completion rings, side by side like the Pokédex page.
  const ringR = 118;
  const ringY = 280;
  drawCompletionRing(ctx, W / 2 - 225, ringY, ringR, spec.rings[0]);
  drawCompletionRing(ctx, W / 2 + 225, ringY, ringR, spec.rings[1]);

  // Headline under the rings.
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = `800 60px ${FONT}`;
  ctx.fillStyle = INK;
  ctx.fillText("Do you have any of these", W / 2, 575);
  ctx.fillText("Pokémon for trading?", W / 2, 648);
  ctx.restore();

  // Up to three missing species, full-colour sprites. The scan frame encloses
  // the WHOLE subject — sprites, names, and the variant pills under them —
  // so the brackets read as framing the ask, not just the artwork.
  const needs = spec.needs.slice(0, 3);
  const tile = 220;
  const gap = 32;
  const groupW = needs.length * tile + (needs.length - 1) * gap;
  const leftX = (W - groupW) / 2;
  const tileY = 716;
  const frameTop = tileY - 34;
  const frameBottom = tileY + tile + 150; // past names (+62) and pills (+84..128)
  drawScanFrame(
    ctx,
    W / 2,
    (frameTop + frameBottom) / 2,
    groupW + 96,
    frameBottom - frameTop,
  );

  const imgs = await Promise.all(needs.map((side) => loadSpriteForSide(side)));
  needs.forEach((side, i) => {
    drawSpriteTile(ctx, leftX + i * (tile + gap), tileY, tile, side, imgs[i], "");
  });

  // Footer CTA: the OFFICIAL App Store badge (this card goes to people who may
  // not have the app — the store is the next step, and the URL travels in the
  // share caption since a PNG can't be tapped).
  const badge = await loadSameOrigin(appStoreBadgeUrl);
  const badgeW = 390;
  const badgeH = 130;
  const badgeY = DEX_NEEDS_H - 180;
  if (badge) {
    drawImageContain(ctx, badge, (W - badgeW) / 2, badgeY, badgeW, badgeH);
  }
}

/**
 * Canvas twin of DexCompletionRing: same colour ladder, same truncate-never-
 * round percent rule (948/951 must NOT read "100%" — the last percent is
 * earned), caught/total tucked under the numeral, caption lines under the ring.
 */
function drawCompletionRing(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  ring: DexNeedsRing,
): void {
  const clamped = Math.min(100, Math.max(0, ring.percent));
  const shownPercent = clamped >= 100 ? 100 : Math.min(99, Math.floor(clamped));
  // DexCompletionRing's ladder with the CSS-var tiers pinned to raw colours
  // (a canvas has no var(--accent-fill); the hsl mirrors its glow fallback).
  const tone =
    clamped <= 40
      ? "hsl(0 75% 55%)"
      : clamped <= 70
        ? "hsl(28 90% 55%)"
        : clamped <= 85
          ? "hsl(50 95% 55%)"
          : clamped <= 95
            ? "hsl(110 70% 55%)"
            : clamped <= 99
              ? "hsl(142 65% 45%)"
              : NEON;
  // All interior metrics scale with the radius so the ring reads the same at
  // any size (mirrors DexCompletionRing, whose text is in viewBox units).
  const strokeW = r * 0.2;

  ctx.save();
  // Track.
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(230,255,244,0.12)";
  ctx.lineWidth = strokeW;
  ctx.stroke();
  // Fill arc from 12 o'clock.
  if (clamped > 0) {
    ctx.beginPath();
    ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + (clamped / 100) * Math.PI * 2);
    ctx.strokeStyle = tone;
    ctx.lineWidth = strokeW;
    ctx.lineCap = "round";
    ctx.shadowColor = tone;
    ctx.shadowBlur = r * 0.16;
    ctx.stroke();
  }
  ctx.restore();

  // Percent numeral, dead centre.
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `800 ${Math.round(r * 0.64)}px ${FONT}`;
  ctx.fillStyle = INK;
  const pctText = String(shownPercent);
  const pctW = ctx.measureText(pctText).width;
  ctx.fillText(pctText, cx, cy - r * 0.04);
  ctx.font = `400 ${Math.round(r * 0.27)}px ${FONT}`;
  ctx.fillText("%", cx + pctW / 2 + r * 0.17, cy - r * 0.04);
  // caught / total under the numeral, inside the ring.
  ctx.font = `400 ${Math.round(r * 0.19)}px ${FONT}`;
  ctx.fillStyle = DIM;
  ctx.fillText(
    `${ring.caught.toLocaleString()} / ${ring.total.toLocaleString()}`,
    cx,
    cy + r * 0.5,
  );
  ctx.restore();

  // Caption lines under the ring.
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = `700 26px ${FONT}`;
  ctx.fillStyle = DIM;
  const capY = cy + r + 56;
  ctx.fillText(spaceOut(ring.label), cx, capY);
  if (ring.sublabel) ctx.fillText(spaceOut(ring.sublabel), cx, capY + 36);
  ctx.restore();
}

/** Approximate the ring caption's letter-spaced uppercase in canvas text. */
function spaceOut(s: string): string {
  return s.toUpperCase().split("").join(" ");
}

// ── Chrome ───────────────────────────────────────────────────────────────────

function drawBackground(ctx: CanvasRenderingContext2D, h: number = H): void {
  ctx.fillStyle = "#040f0a";
  ctx.fillRect(0, 0, W, h);

  const glow = ctx.createRadialGradient(W * 0.5, h * 0.42, 60, W * 0.5, h * 0.42, h * 0.62);
  glow.addColorStop(0, "rgba(26,96,63,0.78)");
  glow.addColorStop(0.45, "rgba(11,44,30,0.5)");
  glow.addColorStop(1, "rgba(3,11,8,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, h);

  // Bottom vignette for footer legibility.
  const vign = ctx.createLinearGradient(0, h * 0.7, 0, h);
  vign.addColorStop(0, "rgba(2,8,5,0)");
  vign.addColorStop(1, "rgba(2,8,5,0.7)");
  ctx.fillStyle = vign;
  ctx.fillRect(0, h * 0.7, W, h * 0.3);
}

function drawParticles(ctx: CanvasRenderingContext2D, h: number = H): void {
  // Deterministic-enough bokeh; a fixed seed keeps a single card stable across
  // its own draw passes (each render reseeds, which is fine — no comparison).
  let seed = 0x9e3779b9;
  const rnd = () => {
    seed ^= seed << 13;
    seed ^= seed >>> 17;
    seed ^= seed << 5;
    return ((seed >>> 0) % 100000) / 100000;
  };
  ctx.save();
  for (let i = 0; i < 48; i++) {
    const x = rnd() * W;
    const y = rnd() * h;
    const r = 2 + rnd() * 8;
    const a = 0.05 + rnd() * 0.3;
    const greenish = rnd() > 0.5;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = greenish ? `rgba(120,240,180,${a})` : `rgba(230,255,244,${a})`;
    ctx.shadowColor = greenish ? "rgba(80,240,160,0.6)" : "rgba(230,255,244,0.5)";
    ctx.shadowBlur = r * 2.4;
    ctx.fill();
  }
  ctx.restore();
}

function drawWordmark(ctx: CanvasRenderingContext2D, y = 150): void {
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = `800 44px ${FONT}`;
  // "NEXUS DEX" with a neon "NEXUS" and a lighter "DEX", letter-spaced manually.
  const label = "NEXUS DEX";
  const spacing = 8;
  const widths = [...label].map((ch) => ctx.measureText(ch).width + spacing);
  const total = widths.reduce((s, w) => s + w, 0) - spacing;
  let x = W / 2 - total / 2;
  [...label].forEach((ch, i) => {
    ctx.fillStyle = i < 5 ? NEON : INK; // "NEXUS" green, " DEX" light
    ctx.fillText(ch, x + widths[i] / 2 - spacing / 2, y);
    x += widths[i];
  });
  ctx.restore();
}

function drawHeadline(ctx: CanvasRenderingContext2D, text: string, y: number): void {
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = `800 96px ${FONT}`;
  ctx.fillStyle = INK;
  const lines = text.split("\n");
  const lineH = 108;
  lines.forEach((line, i) => {
    ctx.fillText(line, W / 2, y + i * lineH);
  });
  ctx.restore();
}

function drawScanFrame(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  w: number,
  h: number,
): void {
  const left = cx - w / 2;
  const right = cx + w / 2;
  const top = cy - h / 2;
  const bottom = cy + h / 2;
  const arm = Math.min(w, h) * 0.17;
  ctx.save();
  ctx.strokeStyle = NEON;
  ctx.lineWidth = 12;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.shadowColor = "rgba(57,245,143,0.75)";
  ctx.shadowBlur = 26;
  const corner = (hx: number, hy: number, dx: number, dy: number) => {
    ctx.beginPath();
    ctx.moveTo(hx + dx * arm, hy);
    ctx.lineTo(hx, hy);
    ctx.lineTo(hx, hy + dy * arm);
    ctx.stroke();
  };
  corner(left, top, 1, 1);
  corner(right, top, -1, 1);
  corner(left, bottom, 1, -1);
  corner(right, bottom, -1, -1);
  ctx.restore();
}

function drawSpriteTile(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  side: TradeCardSide,
  img: CanvasImageSource | null,
  caption: string,
): void {
  const shiny = isShinyVariant(side.variant);
  const tone = shiny ? "#f5c453" : NEON;

  ctx.save();
  roundRectPath(ctx, x, y, size, size, 36);
  ctx.fillStyle = "rgba(6,20,14,0.85)";
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = shiny ? "rgba(245,196,83,0.6)" : "rgba(57,245,143,0.5)";
  ctx.stroke();
  ctx.restore();

  // Caption above the tile: the trainer whose Pokémon this is, in the ink
  // the species name uses below, so the pair reads name, Pokémon, name,
  // Pokémon down each column.
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = `800 34px ${FONT}`;
  ctx.fillStyle = INK;
  ctx.fillText(fitText(ctx, caption, size + 40), x + size / 2, y - 28);
  ctx.restore();

  const pad = 34;
  if (img) {
    drawImageContain(ctx, img, x + pad, y + pad, size - pad * 2, size - pad * 2);
  } else {
    // Fallback: a small, dim #N so a sprite miss never dominates the tile.
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `700 40px ${FONT}`;
    ctx.fillStyle = "rgba(160,220,190,0.3)";
    ctx.fillText(`#${side.speciesId}`, x + size / 2, y + size / 2);
    ctx.restore();
  }

  // Species name.
  ctx.save();
  ctx.textAlign = "center";
  ctx.font = `800 40px ${FONT}`;
  ctx.fillStyle = INK;
  ctx.fillText(fitText(ctx, side.name, size + 20), x + size / 2, y + size + 62);
  ctx.restore();

  // Variant pill under the name. This is the card's ONLY dex-variant marker
  // (the header chip deliberately dropped its label on the assumption this
  // pill carried it), so it must render for EVERY variant — the old
  // shiny-only badge left Lucky / 100% / Shadow / ... cards with no variant
  // chip anywhere (2026-08-26 report). Main stays bare: the base dex has no
  // in-game marker either.
  if (side.variant !== "pokemon" && side.variant !== "complete") {
    const raw = VARIANT_PILL_HSL[side.variant] ?? null;
    const pillTone = raw ? `hsl(${raw})` : tone;
    const pillFill = raw ? `hsl(${raw} / 0.16)` : "rgba(57,245,143,0.16)";
    const pillStroke = raw ? `hsl(${raw} / 0.6)` : "rgba(57,245,143,0.6)";
    const label = `${shiny ? "✦ " : ""}${getVariantLabel(side.variant).toUpperCase()}`;
    ctx.save();
    ctx.font = `800 22px ${FONT}`;
    const bw = Math.max(128, ctx.measureText(label).width + 44);
    const bh = 44;
    const bx = x + size / 2 - bw / 2;
    const by = y + size + 84;
    roundRectPath(ctx, bx, by, bw, bh, 22);
    ctx.fillStyle = pillFill;
    ctx.fill();
    ctx.strokeStyle = pillStroke;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = pillTone;
    ctx.fillText(label, x + size / 2, by + bh / 2 + 1);
    ctx.restore();
  }
}

/**
 * Per-variant pill tint, as raw HSL triplets: a canvas cannot resolve the
 * app's `var(--shiny)`-style tokens, so these pin the DARK-theme values from
 * index.css / DEX_VARIANT_META (same pattern as the completion-ring ladder
 * above). Missing entries fall back to the shiny gold / neon pair.
 */
const VARIANT_PILL_HSL: Record<string, string> = {
  shiny: "38 95% 55%",
  lucky: "43 92% 55%",
  perfect: "326 80% 60%",
  xxs: "265 70% 66%",
  xxl: "16 85% 58%",
  mega: "330 72% 62%",
  gmax: "312 68% 60%",
  dynamax: "0 78% 62%",
  shadow: "280 70% 65%",
  purified: "197 76% 58%",
  shundo: "286 85% 70%",
  shinyLucky: "50 92% 56%",
};

function drawSwapArrow(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.save();
  ctx.strokeStyle = NEON;
  ctx.fillStyle = NEON;
  ctx.lineWidth = 9;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.shadowColor = "rgba(57,245,143,0.7)";
  ctx.shadowBlur = 20;
  ctx.beginPath();
  ctx.moveTo(cx - 44, cy);
  ctx.lineTo(cx + 44, cy);
  ctx.stroke();
  // arrowhead
  ctx.beginPath();
  ctx.moveTo(cx + 44, cy);
  ctx.lineTo(cx + 18, cy - 22);
  ctx.moveTo(cx + 44, cy);
  ctx.lineTo(cx + 18, cy + 22);
  ctx.stroke();
  ctx.restore();
}

async function drawFooter(ctx: CanvasRenderingContext2D): Promise<void> {
  /**
   * NEXUS DEX BRANDING, not a store badge.
   *
   * The footer used to be the official "Download on the App Store" artwork. On a
   * card you send to a friend, that is an advert for Apple: the biggest, most
   * finished-looking element on the image belonged to someone else's brand, and
   * it could not even be tapped — a PNG has no links, so the real store URL rode
   * in the share text anyway. Replaced with our own mark and wordmark, which is
   * what the card is actually for. The store link still travels in the caption.
   */
  const logo = await loadSameOrigin(logoMarkUrl);
  const markSize = 160;
  const markX = (W - markSize) / 2;
  const markY = H - 400;
  if (logo) {
    ctx.save();
    ctx.shadowColor = "rgba(57,245,143,0.45)";
    ctx.shadowBlur = 36;
    drawImageContain(ctx, logo, markX, markY, markSize, markSize);
    ctx.restore();
  }

  // Wordmark under the mark, in the same two-tone treatment the header uses so
  // the card opens and closes on the same signature.
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = `800 52px ${FONT}`;
  const label = "NEXUS DEX";
  const spacing = 9;
  const widths = [...label].map((ch) => ctx.measureText(ch).width + spacing);
  const total = widths.reduce((sum, w) => sum + w, 0) - spacing;
  let x = W / 2 - total / 2;
  const wordY = markY + markSize + 78;
  [...label].forEach((ch, i) => {
    ctx.fillStyle = i < 5 ? NEON : INK;
    ctx.fillText(ch, x + widths[i] / 2 - spacing / 2, wordY);
    x += widths[i];
  });
  ctx.restore();

  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = `600 32px ${FONT}`;
  ctx.fillStyle = DIM;
  ctx.fillText(SITE_HOST, W / 2, wordY + 52);
  ctx.restore();
}

// ── Image + QR loading ───────────────────────────────────────────────────────

function spriteCandidatesForSide(side: TradeCardSide): string[] {
  const shiny = isShinyVariant(side.variant);
  const out: string[] = [];
  const variant = side.variant as PokedexVariant;
  if (isMegaGmaxVariant(variant)) {
    out.push(...megaGmaxSpriteCandidates(variant, side.speciesId));
  }
  out.push(
    officialArtworkUrl(side.speciesId, { shiny }),
    homeArtworkUrl(side.speciesId, { shiny }),
    officialArtworkUrl(side.speciesId),
    homeArtworkUrl(side.speciesId),
  );
  // De-dupe while preserving order.
  return [...new Set(out)];
}

export async function loadSpriteForSide(side: TradeCardSide): Promise<CanvasImageSource | null> {
  return loadSpriteFromCandidates(spriteCandidatesForSide(side));
}

/**
 * First of `urls` that loads as a canvas-exportable image, or null.
 *
 * The card renderers resolve their own art for anything the species id alone
 * cannot express — a picked regional form, a costume — via the app's ordinary
 * candidate builders (formSpriteCandidates), and hand the list here so it goes
 * through the same CORS-safe loader as everything else.
 */
export async function loadSpriteFromCandidates(
  urls: readonly string[],
): Promise<CanvasImageSource | null> {
  for (const url of urls) {
    const img = await loadDrawable(url);
    if (img) return img;
  }
  return null;
}

/**
 * Load a sprite as a canvas-EXPORTABLE image from the sprites.nexusdex.ai CDN.
 * The CDN sends no `Access-Control-Allow-Origin`, so a plain `fetch(cors)` /
 * `<img crossOrigin>` would reject or taint the canvas (breaking toBlob). On
 * native we go through CapacitorHttp (native networking — not subject to CORS)
 * and hand the bytes back as a same-origin data URL; on web we fall back to a
 * CORS fetch, which starts working the moment the CDN sets ACAO. Any failure
 * resolves null and the caller draws a small #N chip instead.
 */
async function loadDrawable(url: string): Promise<HTMLImageElement | null> {
  try {
    const { Capacitor, CapacitorHttp } = await import("@capacitor/core");
    if (Capacitor.isNativePlatform()) {
      const res = await CapacitorHttp.get({ url, responseType: "blob" });
      if (res.status >= 200 && res.status < 300 && typeof res.data === "string" && res.data) {
        const headers = (res.headers ?? {}) as Record<string, string>;
        const contentType = headers["Content-Type"] || headers["content-type"] || "image/png";
        return await dataUrlToImage(`data:${contentType};base64,${res.data}`);
      }
      return null;
    }
  } catch {
    /* fall through to the web fetch path */
  }
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    return await blobToImage(await res.blob());
  } catch {
    return null;
  }
}

function dataUrlToImage(dataUrl: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

export async function loadSameOrigin(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function blobToImage(blob: Blob): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const obj = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      resolve(img);
      // Revoke on the next tick so the decode has completed.
      setTimeout(() => URL.revokeObjectURL(obj), 0);
    };
    img.onerror = () => {
      URL.revokeObjectURL(obj);
      resolve(null);
    };
    img.src = obj;
  });
}

// ── Canvas helpers ───────────────────────────────────────────────────────────

export function drawImageContain(
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const iw = (img as HTMLImageElement).width || w;
  const ih = (img as HTMLImageElement).height || h;
  const scale = Math.min(w / iw, h / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

export function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function withGlow(
  ctx: CanvasRenderingContext2D,
  color: string,
  blur: number,
  fn: () => void,
): void {
  ctx.save();
  ctx.shadowColor = color;
  ctx.shadowBlur = blur;
  fn();
  ctx.restore();
}

/** Shrink the font until `text` fits `maxWidth`, then return the (unchanged) text. */
function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  let px = 40;
  while (px > 22 && ctx.measureText(text).width > maxWidth) {
    px -= 2;
    ctx.font = `800 ${px}px ${FONT}`;
  }
  return text;
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("canvas.toBlob returned null"));
    }, "image/png");
  });
}
